import { MenuItem, Paper, Select, Stack, TextField } from '@mui/material'
import React, { useState } from 'react'

import Flex from 'components/common/Flex'
import { InputContainer } from 'components/common/InputContainer'
import Text from 'components/common/Text'
import { useTranslation } from 'hooks/use-translation'
import theme from 'theme'
import { DelayNode } from 'types/api/scenarios/scenario'

export function parsedTimeString(value) {
  if (value % 3600 === 0) {
    return `${value / 3600}h`
  } else if (value % 60 === 0) {
    return `${value / 60}m`
  }
  return `${value}s`
}

const multiples = {
  s: 1,
  m: 60,
  h: 3600
}

function getUnitFromDuration(duration: number | string) {
  if (duration === '') {
    return 's'
  }

  const durationNumber = Number(duration)

  if (durationNumber % 3600 === 0) {
    return 'h'
  }

  if (durationNumber % 60 === 0) {
    return 'm'
  }

  return 's'
}

export const EditableDelayNode: React.FC<{
  node: DelayNode
  onUpdate: (delay: string | number) => void
  rowActions: React.ReactNode
}> = ({ node, onUpdate, rowActions, ...rest }) => {
  const { trans } = useTranslation()

  const [duration, setDuration] = useState(node.delay)
  const [timeUnit, setTimeUnit] = useState(getUnitFromDuration(duration))
  const displayValue =
    String(duration) === '' ? '' : duration / multiples[timeUnit]

  const units = [
    { label: trans('scenarios.delay.seconds'), value: 's' },
    { label: trans('scenarios.delay.minutes'), value: 'm' },
    { label: trans('scenarios.delay.hours'), value: 'h' }
  ]

  return (
    <Stack alignItems="center" direction="row" spacing={2}>
      <Paper
        css={{
          maxWidth: 'fit-content',
          margin: `${theme.space[3]}px 0`,
          padding: theme.space[2]
        }}
        data-testid="row-delay-type"
        onClick={(e) => e.stopPropagation()}
        {...rest}
      >
        <Stack direction="row" spacing={2}>
          <TextField
            label="Delay"
            placeholder="Delay"
            size="small"
            value={displayValue}
            variant="outlined"
            onChange={(e) => {
              const newDuration =
                e.target.value === ''
                  ? ''
                  : Number(e.target.value) * multiples[timeUnit]
              setDuration(newDuration)
              onUpdate(newDuration)
            }}
          />

          <Select
            size="small"
            value={timeUnit}
            onChange={(e) => {
              setTimeUnit(e.target.value)
              if (duration !== '') {
                const priorValue = Number(duration) / multiples[timeUnit]
                const newDuration = priorValue * multiples[e.target.value]
                setDuration(newDuration)
                onUpdate(newDuration)
              }
            }}
          >
            {units.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>

          {rowActions}
        </Stack>
      </Paper>
    </Stack>
  )
}

export const DelayGraphNode: React.FC<{
  delay: number | string
}> = ({ delay, ...rest }) => {
  return (
    <Paper
      css={{
        maxWidth: 'fit-content',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'left'
      }}
      data-testid="row-delay-type"
      {...rest}
    >
      <Flex
        css={{
          maxWidth: 125
        }}
      >
        <InputContainer
          css={{
            border: 'none',
            padding: `0 ${theme.space[3]}px`,
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 0,
            justifyContent: 'start'
          }}
        >
          <Text
            as="small"
            css={{
              marginRight: theme.space[2]
            }}
          >
            Delay
          </Text>
          <Text
            as="p"
            color="dark"
            css={{ marginLeft: 'auto' }}
            weight="normal"
          >
            {parsedTimeString(delay)}
          </Text>
        </InputContainer>
      </Flex>
    </Paper>
  )
}
